// frontend/src/components/Navbar/UserMenu.js

import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { theme } from "../../theme";
import { useTranslation } from "react-i18next";
import CountryFlag from "../CountryFlag";
import styled from "styled-components";
import Modal from "../Modal/Modal";
import Typography from "../Typography";
import MenuIcon from "../Icons/MenuIcon";
import HelmetIcon from "../Icons/HelmetIcon";
import FindPlanOutlinedIcon from "../Icons/FindPlanOutlineIcon";
import AddCircleOutlineIcon from "../Icons/AddCircleOutlineIcon";
import MyEventsOutlineIcon from "../Icons/MyEventsOutlineIcon";
import CalendarOutlineIcon from "../Icons/CalendarOutlineIcon";
import ProfileIconOutline from "../Icons/ProfileIconOutline";
import PeopleIcon from "../Icons/PeopleIcon";
import GarageOutlineIcon from "../Icons/GarageOutlineIcon";
import PaidTicketIconOutline from "../Icons/PaidTicketIconOutline";
import HelpCircleOutlineIcon from "../Icons/HelpCircleOutlineIcon";
import SettingsOutlineIcon from "../Icons/SettingsOutlineIcon";
import AmbassadorBadgeOutlineIcon from "../Icons/AmbassadorBadgeOutlineIcon";
import LanguageOutlineIcon from "../Icons/LanguageOutlineIcon";
import ArrowRightIcon from "../Icons/ArrowRightIcon";
import Button from "../Button/Button";
import NotificationOutlineIcon from "../Icons/NotificationOutlineIcon";
import { openUserSettingsModal } from "../../utils/settingsNavigation";

// import PlaceholderIcon from '../Icons/HelmetIcon'; // Reemplázalo por los íconos reales

const UserMenu = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [showLangModal, setShowLangModal] = useState(false);
	const dropdownRef = useRef(null);
	const navigate = useNavigate();
	const location = useLocation();
	const { user, logout } = useAuth();
	const { t, i18n } = useTranslation(["navbar", "common"]);

	const isAuthenticated = !!user;

	const toggleMenu = () => setIsOpen(!isOpen);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const languages = [
		{ code: "es", name: t("common:language.es"), countryCode: "es" },
		{ code: "en", name: t("common:language.en"), countryCode: "gb" },
	];

	const changeLanguage = (code) => {
		i18n.changeLanguage(code);
		setShowLangModal(false);
	};

	const menuItems = [
		{
			label: t("findPlan"),
			path: "/",
			show: true,
			icon: (
				<FindPlanOutlinedIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{
			label: t("createEvent"),
			path: "/create-event",
			show: isAuthenticated,
			icon: (
				<AddCircleOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{
			label: t("people"),
			path: "/people",
			show: isAuthenticated,
			icon: (
				<PeopleIcon height={20} width={20} fill={theme.colors.defaultWeak} />
			),
		},
		{
			label: t("myEvents"),
			path: "/hosted-events",
			show: isAuthenticated,
			icon: (
				<MyEventsOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{
			label: t("calendar"),
			path: "/agenda",
			show: isAuthenticated,
			icon: (
				<CalendarOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{
			label: t("profile"),
			path: `/user/${user?.id}`,
			show: isAuthenticated,
			icon: (
				<ProfileIconOutline
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{
			label: t("notifications"),
			path: `/user/${user?.id}/notifications`,
			show: isAuthenticated,
			icon: (
				<NotificationOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{
			label: t("garage"),
			path: `/user/${user?.id}/garage`,
			show: isAuthenticated,
			icon: (
				<GarageOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{ type: "divider" },
		// Admin entry — only shown when the backend reports `isMotorikAdmin: true`
		// (driven by MOTORIK_ADMIN_EMAILS / MOTORIK_ADMIN_USER_IDS env vars).
		{
			label: "Admin",
			path: "/admin/invoices",
			show: Boolean(user?.isMotorikAdmin),
			icon: (
				<SettingsOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.brandMain}
				/>
			),
		},
		{
			label: t("accountSettings"),
			path: `/user/${user?.id}/settings`,
			openSettingsAsModal: true,
			show: isAuthenticated,
			icon: (
				<SettingsOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{
			type: "language",
			label: t("language"),
			show: true,
			icon: (
				<LanguageOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		// { label: t('language'), path: '/language', show: true, icon: <LanguageOutlineIcon height={20} width={20} fill={theme.colors.defaultWeak} /> },
		{
			label: t("helpCenter"),
			externalUrl: "https://motorik.net/help",
			show: true,
			icon: (
				<HelpCircleOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{
			label: t("ambassadorProgram"),
			externalUrl: "https://motorik.net/ambassadors-program",
			show: true,
			icon: (
				<AmbassadorBadgeOutlineIcon
					height={20}
					width={20}
					fill={theme.colors.defaultWeak}
				/>
			),
		},
		{ type: "divider" },
		{
			type: "special",
			externalUrl: "https://motorik.net/help",
			show: true,
			icon: (
				<PaidTicketIconOutline
					height={20}
					width={20}
					fill={theme.colors.brandMain}
				/>
			),
			title: t("startSelling"),
			subtitle: t("startSellingSubtitle"),
		},
		// { type: 'special', path: '/start-selling', show: true, icon: <PaidTicketIconOutline height={20} width={20} fill={theme.colors.brandMain} />, title: t('startSelling'), subtitle: t('startSellingSubtitle') },
		{ type: "divider" },
		isAuthenticated
			? { label: t("logout"), action: logout, show: true }
			: {
					label: t("signInOrJoin"),
					path: "/signin",
					show: true,
					type: "highlighted",
				},
	];

	const handleClick = (item) => {
		setIsOpen(false);
		if (item.type === "language") {
			setShowLangModal(true);
		} else {
			if (item.externalUrl)
				window.open(item.externalUrl, "_blank", "noopener,noreferrer");
			if (item.openSettingsAsModal) {
				openUserSettingsModal({
					navigate,
					location,
					userId: user?.id || user?._id,
				});
			} else if (item.path) {
				navigate(item.path);
			}
			if (item.action) item.action();
		}
	};

	return (
		<>
			<Wrapper ref={dropdownRef}>
				<TriggerButton
					$variant="outlineInverse"
					onClick={toggleMenu}
					size="l"
					sizeTablet="l"
					sizeMobile="m"
				>
					<MenuIconWrapper
						height={20}
						width={20}
						fill={theme.colors.inverseSubtle}
					/>
					{isAuthenticated &&
						(user?.userAvatar ? (
							<UserAvatar src={user.userAvatar} alt="avatar" />
						) : (
							<EmptyUserAvatar>
								<HelmetAvatar
									height={20}
									width={20}
									fill={theme.colors.inverseSubtle}
								/>
							</EmptyUserAvatar>
						))}
				</TriggerButton>
				<Dropdown $visible={isOpen}>
					{menuItems
						.filter((item) => item.show !== false)
						.map((item, i) => {
							if (item.type === "divider")
								return <Divider key={`divider-${i}`} />;
							if (item.type === "special") {
								return (
									<SpecialItem
										key={`special-${i}`}
										onClick={() => handleClick(item)}
									>
										<SpecialItemHeader>
											<Typography
												$variant="body-2-semibold"
												color={theme.colors.defaultMain}
											>
												{item.title}
											</Typography>
											{item.icon && (
												<SpecialItemIconWrapper>
													{item.icon}
												</SpecialItemIconWrapper>
											)}
										</SpecialItemHeader>
										<Typography
											$variant="body-3-regular"
											color={theme.colors.defaultStrong}
										>
											{item.subtitle}
										</Typography>
									</SpecialItem>
								);
							}
							if (item.type === "highlighted") {
								return (
									<HighlightedItem
										key={`highlighted-${i}`}
										onClick={() => handleClick(item)}
									>
										<Typography $variant="body-2-semibold">
											{item.label}
										</Typography>
										<ArrowRightIcon
											height={20}
											width={20}
											fill={theme.colors.defaultMain}
										/>
									</HighlightedItem>
								);
							}
							return (
								<MenuItem key={i} onClick={() => handleClick(item)}>
									{item.icon && <IconWrapper>{item.icon}</IconWrapper>}
									<Typography $variant="body-2-medium">{item.label}</Typography>
								</MenuItem>
							);
						})}
				</Dropdown>
			</Wrapper>

			{showLangModal && (
				<Modal
					title={t("languageSelector")}
					onClose={() => setShowLangModal(false)}
					maxWidth="300px"
					mobileAlignment="center"
				>
					<LanguageList>
						{languages.map((lang) => (
							<LanguageOption
								key={lang.code}
								onClick={() => changeLanguage(lang.code)}
							>
								<CountryFlag countryCode={lang.countryCode} height="24" />
								<Typography
									$variant="body-1-medium"
									style={{ marginLeft: "8px" }}
								>
									{lang.name}
								</Typography>
							</LanguageOption>
						))}
					</LanguageList>
				</Modal>
			)}
		</>
	);
};

export default UserMenu;

const Wrapper = styled.div`
  position: relative;
`;

const TriggerButton = styled(Button)``;

const HelmetAvatar = styled(HelmetIcon)`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 16px;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 48px;
  right: 0;
  z-index: 100;
  width: 240px;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.08);
  padding: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-direction: column;

  opacity: ${({ $visible }) => ($visible ? "1" : "0")};
  transform: ${({ $visible }) => ($visible ? "translateY(0)" : "translateY(-8px)")};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    top: 40px;
  }

  `;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.sizing.xs};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.xs};
  transition: background-color 0.2s;
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const HighlightedItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.xs};
  // background-color: ${({ theme }) => theme.fill.brandAlphaMain16};
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpecialItemIconWrapper = styled(IconWrapper)`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background-color: ${({ theme }) => theme.fill.brandAlphaMain16};
  flex-shrink: 0;
`;

const SpecialItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.xs};
  background-color: ${({ theme }) => theme.fill.defaultMain};
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const SpecialItemHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xxs};
  justify-content: space-between;
  align-items: flex-start;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.border.defaultSubtle};
  margin: ${({ theme }) => theme.sizing.xs} 0;
`;

const UserAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 20px;
    height: 20px;
    border-radius: 20px;
  }
`;

const EmptyUserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.fill.inverseSubtle};
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 20px;
    height: 20px;
  }
`;

const LanguageList = styled.div`
  display: flex; 
  flex-direction: column;
  width: 100%;
  padding: ${({ theme }) => theme.sizing.sm};
  gap: ${({ theme }) => theme.sizing.xs};
`;
const LanguageOption = styled.div`
  display: flex; 
  align-items: center; 
  padding: ${({ theme }) => theme.sizing.xs};
  cursor: pointer; 
  border-radius: ${({ theme }) => theme.radius.xs};

  &:hover { 
    background-color: ${({ theme }) => theme.fill.defaultWeak}; 
  }
`;

const MenuIconWrapper = styled(MenuIcon)`
  margin: ${({ theme }) => theme.sizing.xxs} 0px;
`;
